import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { categoryAPI, currencyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Fuel,
  UtensilsCrossed,
  Wrench,
  PenLine,
  Film,
  Car,
  Zap,
  Folder,
  DollarSign,
} from 'lucide-react';

const iconOptions = [
  { value: 'fuel', label: 'Fuel', icon: Fuel },
  { value: 'utensils', label: 'Food', icon: UtensilsCrossed },
  { value: 'wrench', label: 'Equipment', icon: Wrench },
  { value: 'pen', label: 'Stationary', icon: PenLine },
  { value: 'film', label: 'Entertainment', icon: Film },
  { value: 'car', label: 'Transport', icon: Car },
  { value: 'zap', label: 'Utilities', icon: Zap },
  { value: 'folder', label: 'Other', icon: Folder },
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: 'folder' });
  const [saving, setSaving] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await currencyAPI.getSupportedCurrencies();
      setCurrencies(response.data.currencies || []);
    } catch (error) {
      // Default currencies if API fails
      setCurrencies(['CAD', 'USD', 'EUR', 'GBP']);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCurrencies();
  }, []);

  const handleCurrencyChange = async (newCurrency) => {
    setSavingCurrency(true);
    try {
      await updateUser({ home_currency: newCurrency });
      toast.success(`Home currency updated to ${newCurrency}`);
    } catch (error) {
      toast.error('Failed to update currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ name: category.name, icon: category.icon });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', icon: 'folder' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, formData);
        toast.success('Category updated');
      } else {
        await categoryAPI.create(formData);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await categoryAPI.delete(categoryId);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const defaultCategories = categories.filter((c) => c.is_default);
  const customCategories = categories.filter((c) => !c.is_default);

  const getIconComponent = (iconName) => {
    const option = iconOptions.find((o) => o.value === iconName);
    return option?.icon || Folder;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <Header />
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">Manage your account and preferences</p>
          </div>

          {/* Profile Card */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Account Type</Label>
                <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Currency Settings Card */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Currency Settings
              </CardTitle>
              <CardDescription>
                Set your home currency. Foreign currency expenses will be converted automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Home Currency</Label>
                <Select
                  value={user?.home_currency || 'CAD'}
                  onValueChange={handleCurrencyChange}
                  disabled={savingCurrency}
                >
                  <SelectTrigger className="w-full bg-input/50 border-white/10" data-testid="currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {currencies.map((curr) => (
                      <SelectItem key={curr} value={curr}>
                        {curr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  All expenses will be stored and displayed in this currency. When you scan a receipt in a different currency, it will be automatically converted using the exchange rate at the time of upload.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Categories Card */}
          <Card className="bg-card border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Categories</CardTitle>
                <CardDescription>Manage your expense categories</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => handleOpenDialog()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                    data-testid="add-category-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader>
                    <DialogTitle>
                      {editingCategory ? 'Edit Category' : 'New Category'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingCategory
                        ? 'Update your custom category'
                        : 'Create a new expense category'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Category name"
                        className="bg-input/50 border-white/10"
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <Select
                        value={formData.icon}
                        onValueChange={(value) => setFormData({ ...formData, icon: value })}
                      >
                        <SelectTrigger
                          className="bg-input/50 border-white/10"
                          data-testid="category-icon-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {iconOptions.map((option) => {
                            const IconComp = option.icon;
                            return (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center gap-2">
                                  <IconComp className="w-4 h-4" />
                                  {option.label}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setDialogOpen(false)}
                      className="border-white/10"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="save-category-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Default Categories */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Default Categories</h3>
                <div className="space-y-2">
                  {defaultCategories.map((category) => {
                    const IconComp = getIconComponent(category.icon);
                    return (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                            <IconComp className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">Default</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Categories */}
              {customCategories.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Your Categories</h3>
                  <div className="space-y-2">
                    {customCategories.map((category) => {
                      const IconComp = getIconComponent(category.icon);
                      return (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-white/5"
                          data-testid={`category-${category.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                              <IconComp className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <span className="font-medium">{category.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(category)}
                              className="h-8 w-8 hover:bg-white/5"
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                  data-testid={`delete-category-${category.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-card border-white/10">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete category?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the category "{category.name}".
                                    Expenses using this category will not be deleted.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-secondary border-white/10">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(category.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
